<template>
  <details class="search-condition-accordion">
    <summary class="search-condition-accordion__summary">
      検索条件を変更する
    </summary>
    <div class="search-condition-accordion__content">
      <div class="search-condition-accordion__form">
        <!-- 年代・性別（2カラム） -->
        <div class="search-condition-accordion__row">
          <!-- 年代 -->
          <div class="search-condition-accordion__field">
            <label :for="ageSelectId" class="search-condition-accordion__label">
              年代（任意）
            </label>
            <select
              :id="ageSelectId"
              :value="formData.targetAge"
              name="targetAge"
              class="search-condition-accordion__select"
              @change="handleAgeChange"
            >
              <option
                v-for="option in AGE_OPTIONS"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>

          <!-- 性別 -->
          <div class="search-condition-accordion__field">
            <label
              :for="genderSelectId"
              class="search-condition-accordion__label"
            >
              性別（任意）
            </label>
            <select
              :id="genderSelectId"
              :value="formData.targetGender"
              name="targetGender"
              class="search-condition-accordion__select"
              @change="handleGenderChange"
            >
              <option
                v-for="option in GENDER_OPTIONS"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>

        <!-- 知りたいこと -->
        <div class="search-condition-accordion__field">
          <label
            :for="categorySelectId"
            class="search-condition-accordion__label"
          >
            知りたいこと
          </label>
          <select
            :id="categorySelectId"
            :value="formData.category ?? ''"
            name="category"
            class="search-condition-accordion__select"
            @change="handleCategoryChange"
          >
            <option
              v-for="option in CATEGORY_OPTIONS"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>

        <!-- いつから -->
        <div class="search-condition-accordion__field">
          <label
            :for="durationSelectId"
            class="search-condition-accordion__label"
          >
            いつから
          </label>
          <select
            :id="durationSelectId"
            :value="formData.duration ?? ''"
            name="duration"
            class="search-condition-accordion__select"
            @change="handleDurationChange"
          >
            <option
              v-for="option in DURATION_OPTIONS"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>

        <!-- 受診状況 -->
        <div class="search-condition-accordion__field">
          <label
            :for="medicalVisitSelectId"
            class="search-condition-accordion__label"
          >
            受診状況
          </label>
          <select
            :id="medicalVisitSelectId"
            :value="formData.medicalVisit ?? ''"
            name="medicalVisit"
            class="search-condition-accordion__select"
            @change="handleMedicalVisitChange"
          >
            <option
              v-for="option in MEDICAL_VISIT_OPTIONS"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>

        <!-- 自由入力 -->
        <div class="search-condition-accordion__field">
          <label :for="freeTextId" class="search-condition-accordion__label">
            詳しい状況（任意）
          </label>
          <textarea
            :id="freeTextId"
            :value="formData.freeText"
            name="freeText"
            class="search-condition-accordion__textarea"
            maxlength="470"
            @input="handleFreeTextChange"
          />
          <div class="search-condition-accordion__counter">
            残り{{ remainingChars }}文字
          </div>
        </div>

        <!-- 検索ボタン -->
        <div class="search-condition-accordion__actions">
          <button
            type="button"
            class="button button--primary button--full button--lg"
            :disabled="isLoading"
            @click="handleSearch"
          >
            この条件で再検索する
            <i class="material-icons" aria-hidden="true">search</i>
          </button>
        </div>
      </div>
    </div>
  </details>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type {
  InquiryFormData,
  TargetAge,
  TargetGender,
  CategoryOption,
  DurationOption,
  MedicalVisitOption,
} from '../types';
import { VALIDATION } from '../types';

defineProps<{
  isLoading: boolean;
}>();

const emit = defineEmits<{
  search: [];
}>();

// formData を v-model として管理
const formData = defineModel<InquiryFormData>({ required: true });

// 一意な ID 生成用
const uid = Math.random().toString(36).substring(2, 9);
const ageSelectId = `age-select-${uid}`;
const genderSelectId = `gender-select-${uid}`;
const categorySelectId = `category-select-${uid}`;
const durationSelectId = `duration-select-${uid}`;
const medicalVisitSelectId = `medical-visit-select-${uid}`;
const freeTextId = `free-text-${uid}`;

// 年代選択肢
const AGE_OPTIONS: Array<{ value: TargetAge; label: string }> = [
  { value: 'NOT_SELECTED', label: '年代を選択' },
  { value: 'INFANTS', label: '乳幼児' },
  { value: 'UNDER_10', label: '10歳未満' },
  { value: 'RANGE_10_19', label: '10代' },
  { value: 'RANGE_20_29', label: '20代' },
  { value: 'RANGE_30_39', label: '30代' },
  { value: 'RANGE_40_49', label: '40代' },
  { value: 'RANGE_50_59', label: '50代' },
  { value: 'RANGE_60_69', label: '60代' },
  { value: 'OVER_70', label: '70代以上' },
];

// 性別選択肢
const GENDER_OPTIONS: Array<{ value: TargetGender; label: string }> = [
  { value: 'NOT_SELECTED', label: '性別を選択' },
  { value: 'F', label: '女性' },
  { value: 'M', label: '男性' },
];

// カテゴリ選択肢
const CATEGORY_OPTIONS: Array<{ value: CategoryOption | ''; label: string }> = [
  { value: 'should_go_hospital', label: '病院に行くべき？' },
  { value: 'which_department', label: '何科に行けばいい？' },
  { value: 'cause', label: '原因は？' },
  { value: 'how_to_deal', label: '対処法は？' },
  { value: 'medicine_treatment', label: '薬や治療法は？' },
  { value: 'other', label: 'その他' },
  { value: '', label: '選択しない' },
];

// 期間選択肢
const DURATION_OPTIONS: Array<{ value: DurationOption | ''; label: string }> = [
  { value: 'today', label: '今日初めて気づいた' },
  { value: 'yesterday', label: '昨日から' },
  { value: 'few_days', label: '2,3日以内' },
  { value: 'within_week', label: '1週間以内から' },
  { value: 'over_week', label: '1週間以上前から' },
  { value: 'no_symptom', label: '症状は無い・当てはまらない' },
  { value: '', label: '選択しない' },
];

// 受診状況選択肢
const MEDICAL_VISIT_OPTIONS: Array<{
  value: MedicalVisitOption | '';
  label: string;
}> = [
  { value: 'before', label: '受診前' },
  { value: 'during', label: '通院中（治療中）' },
  { value: 'after', label: '受診後' },
  { value: '', label: '選択しない' },
];

// 残り文字数
const remainingChars = computed(() => {
  const currentLength = formData.value?.freeText?.length || 0;
  return VALIDATION.MAX_FREE_TEXT_LENGTH - currentLength;
});

// 各フィールドの変更ハンドラ
const updateFormData = (updates: Partial<InquiryFormData>) => {
  formData.value = { ...formData.value, ...updates };
};

const handleAgeChange = (event: Event) => {
  const target = event.target as HTMLSelectElement;
  updateFormData({ targetAge: target.value as TargetAge });
};

const handleGenderChange = (event: Event) => {
  const target = event.target as HTMLSelectElement;
  updateFormData({ targetGender: target.value as TargetGender });
};

const handleCategoryChange = (event: Event) => {
  const target = event.target as HTMLSelectElement;
  updateFormData({
    category: target.value === '' ? null : (target.value as CategoryOption),
  });
};

const handleDurationChange = (event: Event) => {
  const target = event.target as HTMLSelectElement;
  updateFormData({
    duration: target.value === '' ? null : (target.value as DurationOption),
  });
};

const handleMedicalVisitChange = (event: Event) => {
  const target = event.target as HTMLSelectElement;
  updateFormData({
    medicalVisit:
      target.value === '' ? null : (target.value as MedicalVisitOption),
  });
};

const handleFreeTextChange = (event: Event) => {
  const target = event.target as HTMLTextAreaElement;
  updateFormData({ freeText: target.value });
};

const handleSearch = () => {
  emit('search');
};
</script>

<style lang="scss" scoped>
@import '~green/_variables.scss';

.search-condition-accordion {
  border: 1px solid $base-border-color;
  border-radius: $border-radius-base;
  background-color: #fff;
  margin-top: 16px;

  &__summary {
    padding: 16px;
    font-weight: bold;
    font-size: 0.875rem;
    color: $primary-color;
    cursor: pointer;
    list-style: none;
    display: flex;
    align-items: center;
    justify-content: space-between;

    &::after {
      content: '';
      width: 8px;
      height: 8px;
      border-right: 2px solid $primary-color;
      border-bottom: 2px solid $primary-color;
      transform: rotate(45deg);
      transition: transform 0.3s ease-out;
    }

    &::-webkit-details-marker {
      display: none;
    }
  }

  &[open] &__summary::after {
    transform: rotate(-135deg);
  }

  &__content {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 0.3s ease-out;
    overflow: hidden;
  }

  &[open] &__content {
    grid-template-rows: 1fr;
  }

  &__form {
    min-height: 0;
    padding: 0 16px 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    box-sizing: border-box;
  }

  &__row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0; // はみ出し防止
  }

  &__label {
    font-size: 0.875rem;
    font-weight: bold;
    color: $base-font-color;
  }

  &__select {
    width: 100%;
    padding: 10px 12px;
    font-size: 1rem;
    border: 1px solid $base-border-color;
    border-radius: $border-radius-base;
    background-color: #fff;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
    cursor: pointer;
    box-sizing: border-box;
    color: inherit;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    -webkit-appearance: none;
    -moz-appearance: none;

    &:-moz-focusring {
      color: transparent;
      text-shadow: 0 0 0 #333;
    }

    &:focus {
      outline: none;
      border-color: $primary-color;
      box-shadow: 0 0 0 2px rgba($primary-color, 0.2);
    }
  }

  &__textarea {
    width: 100%;
    padding: 12px;
    border: 1px solid $base-border-color;
    border-radius: $border-radius-base;
    font-size: 1rem;
    font-family: inherit;
    resize: vertical;
    min-height: 100px;
    box-sizing: border-box;

    &:focus {
      outline: none;
      border-color: $primary-color;
      box-shadow: 0 0 0 2px rgba($primary-color, 0.2);
    }
  }

  &__counter {
    text-align: right;
    font-size: 0.75rem;
    color: $tertiary-color;
  }

  &__actions {
    margin-top: 8px;

    .button {
      box-sizing: border-box;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;

      .material-icons {
        font-size: 1.875rem;
      }
    }
  }
}
</style>
